import {addActionPreview, removeActionPreview, synchronizeOnInput as synchronizeAction} from "./Actions/Preview/Synchronizer";
import {initializeRecipientHint, synchronizeRecipientHint, updateRecipientHints} from "./Actions/RecipientHint";
import {addFilterPreview, removeFilterPreview, removeGroupPreview, synchronizeOnInput as synchronizeFilter, synchronizeFilters, animateCrossGroupMove, animateMatchChange} from "./Filters/Preview/Synchronizer";
import {sort} from "./Actions/Sorter/Sort";
import {initializeRemoteOptions} from "./Actions/RemoteOptions";
import {fromString} from "../ToastMessage";
import {revealDependentsByValue} from "../Form/FieldReveal";
import {animateByClass} from "../../Library/Animation";

const PIXELS_PER_LINE = 16;

const attachedPanels = new WeakSet();

const revealActionDependents = event => {
    const action = event.target.closest('.automation-action');

    if (action) {
        revealDependentsByValue(event.target, action);
    }
};

const isInRect = (event, rect) => (
    event.clientX >= rect.left
    && event.clientX <= rect.right
    && event.clientY >= rect.top
    && event.clientY <= rect.bottom
);

const wheelDelta = (event, scrollContainer) => {
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) {
        return {
            x: event.deltaX * PIXELS_PER_LINE,
            y: event.deltaY * PIXELS_PER_LINE,
        };
    }

    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
        return {
            x: event.deltaX * scrollContainer.clientWidth,
            y: event.deltaY * scrollContainer.clientHeight,
        };
    }

    return {
        x: event.deltaX,
        y: event.deltaY,
    };
};

const scrollWorkflowThroughMask = (event) => {
    if (!event.target.closest('.mask')) {
        return;
    }

    const workflow = document.querySelector('.workflow');

    if (!workflow || !isInRect(event, workflow.getBoundingClientRect())) {
        return;
    }

    if (!document.querySelector('.workflow-container .automation.preview')) {
        return;
    }

    event.preventDefault();

    const delta = wheelDelta(event, workflow);

    workflow.scrollBy({
        left: delta.x,
        top:  delta.y,
    });
};

const scrollPreviewIntoView = (preview) => {
    setTimeout(() => {
        const scrollContainer = document.querySelector('.workflow');

        if (!scrollContainer) {
            return;
        }

        const previewRect   = preview.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const padding       = 20;

        if (previewRect.bottom <= containerRect.bottom - padding) {
            return;
        }

        scrollContainer.scrollBy({
            top:      previewRect.bottom - containerRect.bottom + padding,
            behavior: 'smooth',
        });
    }, 300);
};

const moveAutomation = async (event) => {
    if (!event.target.closest('.automation')) {
        return;
    }

    const url = event.target.dataset.sortUrl.replace('{0}', event.detail.destinationIndex.toString());

    const form = new FormData();

    document.querySelectorAll('.workflow-container .automation').forEach((automation) => {
        form.append('automations[]', parseInt(automation.dataset.automationId, 10));
    });

    const request = new Request(url, {
        method: 'POST',
        body: form,
    });

    const response = await fetch(request);

    if (!response.ok) {
        event.detail.restore();

        fromString(await response.text());
    }
};

const workflowDropStateClasses = ['drop-target', 'drop-blocked', 'drop-hover'];

const markWorkflowDropTargets = (event) => {
    const automation = event.target.closest('.automation');

    if (!automation?.dataset.moveUrl) {
        return;
    }

    document.querySelectorAll('.workflow-list li[data-workflow-id]').forEach((item) => {
        if (item.classList.contains('active')) {
            return;
        }

        const supportedTargets = (item.dataset.supportedTargets ?? '').split(' ');

        item.classList.add(supportedTargets.includes(automation.dataset.targetEntity) ? 'drop-target' : 'drop-blocked');
    });
};

const unmarkWorkflowDropTargets = () => {
    document.querySelectorAll('.workflow-list li[data-workflow-id]').forEach((item) => {
        item.classList.remove(...workflowDropStateClasses);
    });
};

const moveAutomationToWorkflow = async (event) => {
    const automation = event.target.closest('.automation');

    if (!automation) {
        return;
    }

    const destination = event.detail.dropTarget;

    const response = await fetch(automation.dataset.moveUrl.replace('{0}', destination.dataset.workflowId), {
        method: 'POST',
    });

    fromString(await response.text());

    if (!response.ok) {
        return;
    }

    destination.classList.add('drop-accepted');
    destination.addEventListener('animationend', () => destination.classList.remove('drop-accepted'), {once: true});

    automation.remove();
};

const toggleAutomationStatus = async (event) => {
    const wrapper = event.target.closest('.automation .form-toggle-wrap');

    if (!wrapper) {
        return;
    }

    event.stopPropagation();

    const button = wrapper.querySelector('.form-toggle');

    const automation = wrapper.closest('.automation');
    const cards      = automation.classList.contains('preview')
        ? [automation, document.querySelector(`.automation.target[data-automation-id="${automation.dataset.automationId}"]`)]
        : [automation];

    const setActive = (active) => {
        cards.forEach(card => {
            card.querySelector('header .form-toggle').classList.toggle('enabled', active);
            card.classList.toggle('disabled', !active);

            if (!card.classList.contains('preview')) {
                card.classList.toggle('collapsed', !active);
            }
        });
    };

    const active = !button.classList.contains('enabled');

    setActive(active);

    const form = new FormData();

    form.set('active', +active);

    const response = await fetch(wrapper.dataset.url, {
        method: 'post',
        body: form,
    });

    if (!response.ok) {
        setActive(!active);
        fromString(await response.text());
    }
};

const toggleAutomationCollapsed = (event) => {
    const header = event.target.closest('.automation:not(.preview) > header');

    if (!header || event.target.closest('button, a, .form-toggle-wrap, .reorder-handle')) {
        return;
    }

    header.closest('.automation').classList.toggle('collapsed');
};

let maskWheelForwarderAttached = false;

export class Automations {
    constructor(container) {
        this.container   = container;

        if (!maskWheelForwarderAttached) {
            document.addEventListener('wheel', scrollWorkflowThroughMask, {passive: false});
            maskWheelForwarderAttached = true;
        }

        this.container.addEventListener('click', toggleAutomationStatus);
        this.container.addEventListener('click', toggleAutomationCollapsed);

        this.container.addEventListener('sortabledrop', moveAutomation);

        this.container.addEventListener('sortablestart', markWorkflowDropTargets);
        this.container.addEventListener('sortabledroptarget', moveAutomationToWorkflow);
        this.container.addEventListener('sortabledroptarget', unmarkWorkflowDropTargets);
        this.container.addEventListener('sortabledrop', unmarkWorkflowDropTargets);
        this.container.addEventListener('sortablecancel', unmarkWorkflowDropTargets);
    }
}

const sortActions = async (event) => {
    if (!event.target.closest('.automation-action')) {
        return;
    }

    await sort(
        document.querySelector('.workflow .automation.preview .actions ol'),
        event.detail.sourceIndex,
        event.detail.destinationIndex
    );
};

const sortFilters = (event) => {
    if (!event.target.closest('.automation-filter')) {
        return;
    }

    if (event.detail.sourceContainer !== event.detail.destinationContainer) {
        animateCrossGroupMove(event.target);

        return;
    }

    const filterGroup   = event.target.closest('.filter-group');
    const configuration = filterGroup?.closest('.filter-configuration');
    const preview       = document.querySelector('.workflow-container .automation.preview');

    if (!filterGroup || !configuration || !preview) {
        return;
    }

    const groupIndex        = Array.from(configuration.querySelectorAll('.filter-group')).indexOf(filterGroup);
    const previewFilterList = preview.querySelectorAll('.filter-preview-group ol')[groupIndex];

    if (!previewFilterList) {
        return;
    }

    const container = filterGroup.querySelector('.filter-group-filters');

    if (!container) {
        return;
    }

    const filters                = [...container.querySelectorAll(':scope > .automation-filter')];
    const sourceFilterIndex      = Math.floor(event.detail.sourceIndex / 2) + 1;
    const destinationFilterIndex = filters.indexOf(event.target) + 1;

    if (sourceFilterIndex < 1 || destinationFilterIndex < 1 || sourceFilterIndex === destinationFilterIndex) {
        return;
    }

    sort(previewFilterList, sourceFilterIndex, destinationFilterIndex);
};

const flashAddedAction = (event) => animateByClass(event.detail.action, 'action-added');

const scrollPanelToBottom = (event) => {
    const scrollContainer = event.currentTarget.querySelector('.content');

    if (scrollContainer) {
        scrollContainer.scrollTo({
            top: scrollContainer.scrollHeight,
            behavior: 'smooth',
        });
    }
};

document.addEventListener('sidepanel:beforeOpen', event => {
    const panel = event.target.closest?.('.side-panel') ?? document.querySelector('.side-panel');

    if (!panel) {
        return;
    }

    const form = panel.querySelector('#edit-automation');

    if (!form || attachedPanels.has(panel)) {
        return;
    }

    attachedPanels.add(panel);

    const preview = buildPreview(form);

    if (preview) {
        scrollPreviewIntoView(preview);
    }

    initializeRemoteOptions(panel);

    panel.addEventListener('automationactionadded', event => initializeRemoteOptions(event.detail.action));
    panel.addEventListener('automationactionadded', addActionPreview);
    panel.addEventListener('automationactionadded', initializeRecipientHint);
    panel.addEventListener('automationactionadded', scrollPanelToBottom);
    panel.addEventListener('automationactionadded', flashAddedAction);
    panel.addEventListener('automationactionremoved', removeActionPreview);
    panel.addEventListener('automationfilteradded', addFilterPreview);
    panel.addEventListener('automationfilterremoved', removeFilterPreview);
    panel.addEventListener('filtermatchchanged', animateMatchChange);
    panel.addEventListener('filtergroupremoved', removeGroupPreview);
    panel.addEventListener('input', revealActionDependents);
    panel.addEventListener('input', synchronizeAction);
    panel.addEventListener('input', synchronizeRecipientHint);
    panel.addEventListener('change', synchronizeRecipientHint);
    panel.addEventListener('input', synchronizeFilter);
    panel.addEventListener('change', synchronizeFilter);

    updateRecipientHints(panel);

    panel.addEventListener('sortabledrop', sortActions);
    panel.addEventListener('sortabledrop', sortFilters);

    panel.addEventListener('sidepanelsubmitted', synchronizeFilters);

    observeChangeButton(panel);
});

const updateChangeButtonVisibility = (panel) => {
    const changeButton = panel.querySelector('.change-target');

    if (!changeButton) {
        return;
    }

    const hasFilters = panel.querySelector('.filter-configuration .filter-group') !== null;
    const hasActions = panel.querySelector('.action-section .automation-action') !== null;

    changeButton.style.display = (hasFilters || hasActions) ? 'none' : '';
};

const changeButtonObservers = new WeakMap();

const observeChangeButton = (panel) => {
    if (changeButtonObservers.has(panel)) {
        return;
    }

    const observer = new MutationObserver(() => updateChangeButtonVisibility(panel));

    observer.observe(panel, {childList: true, subtree: true});
    changeButtonObservers.set(panel, observer);

    updateChangeButtonVisibility(panel);
};

const unobserveChangeButton = (panel) => {
    const observer = changeButtonObservers.get(panel);

    if (!observer) {
        return;
    }

    observer.disconnect();
    changeButtonObservers.delete(panel);
};

const applyTargetTheme = (automation, form) => {
    const targetValue = form.querySelector('[name="targetEntity"]')?.value;

    if (!targetValue) {
        return;
    }

    automation.classList.forEach(className => {
        if (className.startsWith('target-themed--')) {
            automation.classList.remove(className);
        }
    });
    automation.classList.add(`target-themed--${targetValue}`);

    const targetIcon  = automation.querySelector('.target-icon');
    const summaryIcon = form.closest('.side-panel').querySelector('.target-summary .panel-summary-icon i');

    if (targetIcon && summaryIcon) {
        targetIcon.replaceChildren(summaryIcon.cloneNode(true));
    }
};

const buildPreview = (form) => {
    if (form.hasAttribute('data-id')) {
        const activeAutomation = document.querySelector(`.workflow-container .automation[data-automation-id="${form.dataset.id}"]`)

        activeAutomation.classList.add('target');
        activeAutomation.classList.remove('controls-returning');

        const toggle  = activeAutomation.querySelector('header .form-toggle-wrap');
        const actions = activeAutomation.querySelector('.card-actions');
        const offset  = actions.getBoundingClientRect().right - toggle.getBoundingClientRect().right;

        activeAutomation.style.setProperty('--card-controls-offset', `${offset}px`);

        const preview = activeAutomation.cloneNode(true);

        preview.classList.add('preview');
        preview.classList.remove('collapsed');

        preview.querySelectorAll('.edit-automation, .card-actions').forEach(control => control.inert = true);

        activeAutomation.after(preview);

        activeAutomation.style.display = 'none';

        applyTargetTheme(preview, form);

        return preview;
    }

    const template = document.getElementById('new-automation');

    const lastAutomation = document.querySelector('.workflow-container .automation:last-of-type');

    const fragment = template.content.cloneNode(true);

    if (lastAutomation) {
        lastAutomation.after(fragment);
    } else {
        document.querySelector('.workflow-container .automation-list').prepend(fragment);
    }

    const preview = document.querySelector('.automation.preview');

    applyTargetTheme(preview, form);

    return preview;
};

document.addEventListener('sidepanel:close', (event) => {
    const panel = event.target.closest?.('.side-panel') ?? event.target;

    if (!panel || !panel.querySelector?.('#edit-automation')) {
        return;
    }

    const preview = document.querySelector('.automations .automation.preview');

    if (preview) {
        preview.parentNode.removeChild(preview);
    }

    document.querySelectorAll('.automation.target').forEach(automation => {
        automation.style.display = '';
        automation.classList.remove('target');

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        automation.classList.add('controls-returning');
        const toggle = automation.querySelector('header .form-toggle-wrap');

        toggle.addEventListener('animationend', function clearReturning(event) {
            if (event.target !== toggle) {
                return;
            }

            automation.classList.remove('controls-returning');
            toggle.removeEventListener('animationend', clearReturning);
        });
    });

    panel.removeEventListener('automationactionadded', addActionPreview);
    panel.removeEventListener('automationactionadded', scrollPanelToBottom);
    panel.removeEventListener('automationactionadded', flashAddedAction);
    panel.removeEventListener('automationactionremoved', removeActionPreview);
    panel.removeEventListener('automationfilteradded', addFilterPreview);
    panel.removeEventListener('automationfilterremoved', removeFilterPreview);
    panel.removeEventListener('filtermatchchanged', animateMatchChange);
    panel.removeEventListener('filtergroupremoved', removeGroupPreview);
    panel.removeEventListener('input', revealActionDependents);
    panel.removeEventListener('input', synchronizeAction);
    panel.removeEventListener('input', synchronizeFilter);
    panel.removeEventListener('change', synchronizeFilter);
    panel.removeEventListener('sortabledrop', sortActions);
    panel.removeEventListener('sortabledrop', sortFilters);
    panel.removeEventListener('sidepanelsubmitted', synchronizeFilters);

    attachedPanels.delete(panel);

    unobserveChangeButton(panel);
});
